import React, {useCallback, useState} from "react";
import {useAsync} from "react-async-hook";
import {CellProps, Column} from "react-table"
import arrayMove from 'array-move';
import {MenuSetMenu} from "../../../../types/admin/menuSet";
import {MenuFormModal} from "./MenuFormModal";
import {getMenuTypeText, MenuType} from "../../../../enum/menu/MenuType";
import {MenuAddMethodSelectModal} from "./MenuAddMethodSelectModal";
import {MenuListPreview} from "./MenuListPreview";
import {Table} from "../../common/table/Table";
import {SelectMenuModal} from "../Menu/SelectMenuModal";
import {Menu} from "../../../../types/admin/menu";

interface DataType extends MenuSetMenu {
  operation?: string
}

interface MenuSetMenuInputProps {
  value: MenuSetMenu[];
  labelSetId: number | undefined;
  setExistsSelectedLabel: React.Dispatch<React.SetStateAction<boolean>>;
  onChange: (menuList: MenuSetMenu[]) => Promise<void>;
}

export const MenuListInput = (props: MenuSetMenuInputProps) => {
  const { value: menuList, labelSetId, setExistsSelectedLabel, onChange } = props;
  const [activeMenuList, setActiveMenuList] = useState<DataType[]>([]);
  const [showMenuAddMethodSelectModal, setShowMenuAddMethodSelectModal] = React.useState<boolean>(false);
  const [showMenuFormModal, setShowMenuFormModal] = React.useState<boolean>(false);
  const [showSelectMenuModal, setShowSelectMenuModal] = React.useState<boolean>(false);
  const [activeMenuListNest, setActiveMenuListNest] = React.useState<number[]>([]);
  const defaultNewMenuValues = {
    id: null,
    name: '',
    menuTypeId: MenuType.MATERIAL,
    menuMaterialId: null,
    menuMaterialName: null,
    vhPresentationId: null,
    taskCreateFlg: false,
    taskCoopLabelId: null,
    taskCoopLabelName: null,
    enabledFlg: true,
    menuOrder: 1,
    subMenuList: [],
  };
  const [defaultMenuFormValues, setDefaultMenuFormValues] = React.useState<MenuSetMenu>(defaultNewMenuValues);
  const [isMenuFormEditMode, setIsMenuFormEditMode] = React.useState<boolean>(false);
  const [editRowIndex, setEditRowIndex] = React.useState<number | null>(null);
  const [selectedMenuIdMap, setSelectedMenuIdMap] = React.useState<Map<number, boolean>>(new Map());
  const [selectedMenuMaterialIdMap, setSelectedMenuMaterialIdMap] = React.useState<Map<number, boolean>>(new Map());
  const [selectedVhPresentationIdMap, setSelectedVhPresentationIdMap] = React.useState<Map<string, boolean>>(new Map());
  const [selectedLabelCount, setSelectedLabelCount] = React.useState<number>(0);


  useAsync(async () => {
    setExistsSelectedLabel(selectedLabelCount > 0);
  }, [selectedLabelCount]);

  const handleShowAddMenuMethodSelectModalClick = async (): Promise<void> => {
    setIsMenuFormEditMode(false);
    setEditRowIndex(null);
    setDefaultMenuFormValues({
      ...defaultNewMenuValues,
      menuOrder: activeMenuList.length + 1,
    });
    setShowMenuAddMethodSelectModal(true);
  };

  const handleCreateMenuClick = async (): Promise<void> => {
    setIsMenuFormEditMode(false);
    setEditRowIndex(null);
    setDefaultMenuFormValues({
      ...defaultNewMenuValues,
      menuOrder: activeMenuList.length + 1,
    });
    setShowMenuFormModal(true);
  };

  const handleEditMenuClick = useCallback(async (rowIndex: number): Promise<void> => {
    setIsMenuFormEditMode(true);
    setEditRowIndex(rowIndex);
    setDefaultMenuFormValues(activeMenuList[rowIndex]);
    setShowMenuFormModal(true);
  }, [activeMenuList]);

  const handleSelectMenuClick = async (): Promise<void> => {
    setIsMenuFormEditMode(false);
    setEditRowIndex(null);
    setDefaultMenuFormValues({
      ...defaultNewMenuValues,
      menuOrder: activeMenuList.length + 1,
    });
    setShowSelectMenuModal(true);
  };


  // メニューリストの追加先の階層にメニューを追加したものを返却する。
  const concatMenu = (originalList: MenuSetMenu[], destNest: number[], menu: MenuSetMenu): MenuSetMenu[] => {
    // 下位のネストが存在する（＝追加対象がもっと下のメニュー階層）場合
    if (destNest.length > 0) {
      return originalList.map((original, index) => {
        const destIndex = destNest[0];
        // 置換対象のサブメニューを持たないメニューの場合
        if (index !== destIndex) {
          return original;
        }

        // サブメニューに対して、再帰処理
        const newMenu: MenuSetMenu = original;
        newMenu.subMenuList = concatMenu(
          original.subMenuList,
          destNest.slice(1),
          menu,
        );

        return newMenu;
      });
    }

    // ネストなし（＝追加対象のメニュー階層）の場合、追加対象を追加したリストを返却
    return originalList.concat(menu);
  };

  const addSelectedItems = async (menu: MenuSetMenu): Promise<void> => {
    // 追加対象が既存のメニューの場合
    if (menu.id !== null) {
      const menuId = menu.id;
      setSelectedMenuIdMap((prevState => {
        return (new Map(prevState)).set(menuId, true);
      }));
    }

    // 追加対象が資料メニューの場合
    if ((menu.menuTypeId === MenuType.MATERIAL) && (menu.menuMaterialId !== null)) {
      const { menuMaterialId } = menu;
      setSelectedMenuMaterialIdMap((prevState => {
        return (new Map(prevState)).set(menuMaterialId, true);
      }));

      if (menu.taskCoopLabelName !== null) {
        setSelectedLabelCount((prevState => {
          return prevState + 1 ;
        }));
      }
    }

    // 追加対象が自動プレゼンメニューの場合
    if ((menu.menuTypeId === MenuType.VH_PRESENTATION) && (menu.vhPresentationId !== null)) {
      const { vhPresentationId } = menu;
      setSelectedVhPresentationIdMap((prevState => {
        return (new Map(prevState)).set(vhPresentationId, true);
      }));
    }

    for (let i = 0; i < menu.subMenuList.length; i += 1) {
      // eslint-disable-next-line no-await-in-loop
      await addSelectedItems(menu.subMenuList[i]);
    }
  };

  useAsync(async () => {
    if (menuList.length > 0) {
      setActiveMenuList(menuList);
      for (let i = 0; i < menuList.length; i += 1) {
        // eslint-disable-next-line no-await-in-loop
        await addSelectedItems(menuList[i]);
      }
    }
  }, []);

  // メニューリスト内の指定したネストのメニューリストを置換したものを新しく返却する。
  const trasnsMenuList = useCallback((originalList: MenuSetMenu[], destNest: number[], destList: MenuSetMenu[]): MenuSetMenu[] => {
    // ネストが存在する（＝置換対象がもっと下の階層）場合
    if (destNest.length > 0) {
      return originalList.map((original, index) => {
        const destIndex = destNest[0];
        // 置換対象のサブメニューを持たないメニューの場合
        if (index !== destIndex) {
          return original;
        }

        // サブメニューに対して、再帰処理
        const transMenu: MenuSetMenu = original;
        transMenu.subMenuList = trasnsMenuList(
          original.subMenuList,
          destNest.slice(1),
          destList,
        );

        return transMenu;
      })
    }

    // ネストなし（＝置換対象）の場合、置換対象のリストと置き換え
    return destList;
  }, []);

  // メニューリストから指定したネストのメニューリストを取得する。
  const getMenuList = useCallback((list: MenuSetMenu[], destNest: number[]): MenuSetMenu[] => {
    // ネストが存在する（＝取得対象がもっと下の階層）場合
    if (destNest.length > 0) {
      const findMenu = list.find((menu, index) => {
        const destIndex = destNest[0];
        return index === destIndex;
      });

      if (typeof findMenu === 'undefined') {
        // ToDo: エラーハンドリング、Errorクラス、エラーメッセージを適切なものに
        throw new Error('存在しないネストが指定されました。');
      }

      return getMenuList(
        findMenu.subMenuList,
        destNest.slice(1),
      );
    }

    return list;
  }, []);

  // メニューリスト内の指定したネストのメニューを置換したものを新しく返却する。
  const transMenu = (originalList: MenuSetMenu[], destNest: number[], menu: MenuSetMenu): MenuSetMenu[] => {
    if (destNest.length < 1) {
      // ToDo: エラーハンドリング、Errorクラス、エラーメッセージを適切なものに
      throw new Error('置換対象のネストを指定してください。');
    }

    const destIndex = destNest[destNest.length - 1];
    const transList = getMenuList(originalList, destNest.slice(0, destNest.length - 1))
      .map((original, index) => {
        if (index !== destIndex) {
          return original;
        }

        return menu;
      });

    return trasnsMenuList(originalList, destNest.slice(0, destNest.length - 1), transList);
  };

  const deleteSelectedItems = useCallback(async (menu: MenuSetMenu): Promise<void> => {
    if (menu.id !== null) {
      const menuId = menu.id;
      setSelectedMenuIdMap((prevState => {
        const newMap = new Map(prevState);
        newMap.delete(menuId);
        return newMap;
      }));
    }

    if ((menu.menuTypeId === MenuType.MATERIAL) && (menu.menuMaterialId !== null)) {
      const { menuMaterialId } = menu;
      setSelectedMenuMaterialIdMap((prevState => {
        const newMap = new Map(prevState);
        newMap.delete(menuMaterialId);
        return newMap;
      }));


      if (menu.taskCoopLabelId !== null) {
        setSelectedLabelCount((prevState => {
          return prevState - 1;
        }));

      }
    }

    if ((menu.menuTypeId === MenuType.VH_PRESENTATION) && (menu.vhPresentationId !== null)) {
      const { vhPresentationId } = menu;
      setSelectedVhPresentationIdMap((prevState => {
        const newMap = new Map(prevState);
        newMap.delete(vhPresentationId);
        return newMap;
      }));
    }

    for (let i = 0; i < menu.subMenuList.length; i += 1) {
      // eslint-disable-next-line no-await-in-loop
      await deleteSelectedItems(menu.subMenuList[i]);
    }

  }, []);

  const updateSelectedItems = async (oldMenu: MenuSetMenu, newMenu: MenuSetMenu) => {
    await deleteSelectedItems(oldMenu);
    await addSelectedItems(newMenu);
  };

  // メニューリスト内の指定したネストのメニューを削除したものを新しく返却する。
  const filterMenu = useCallback((originalList: MenuSetMenu[], destNest: number[]): MenuSetMenu[] => {
    if (destNest.length < 1) {
      // ToDo: エラーハンドリング、Errorクラス、エラーメッセージを適切なものに
      throw new Error('削除対象のネストを指定してください。');
    }

    const deleteRowIndex = destNest[destNest.length - 1];
    const transList = getMenuList(originalList, destNest.slice(0, destNest.length - 1))
      .filter((_, index) => {
        return deleteRowIndex !== index;
      })
      .map((menu, index) => {
        return Object.assign(menu, { menuOrder: index + 1 })
      });

    return trasnsMenuList(originalList, destNest.slice(0, destNest.length - 1), transList);
  }, [getMenuList, trasnsMenuList]);

  const handleAddMenu = async (menu: MenuSetMenu): Promise<void> => {
    const newMenuList = concatMenu(menuList, activeMenuListNest, menu);
    await addSelectedItems(menu);
    await onChange(newMenuList);
    setActiveMenuList(getMenuList(newMenuList, activeMenuListNest));
  };

  const handleUpdateMenu = async (menu: MenuSetMenu): Promise<void> => {
    if (editRowIndex === null) {
      // ToDo: エラーハンドリング、Errorクラス、エラーメッセージを適切なものに
      throw new Error('更新対象の行インデックスを指定してください。');
    }

    const newMenuList = transMenu(menuList, activeMenuListNest.concat(editRowIndex), menu);
    await updateSelectedItems(activeMenuList[editRowIndex], menu);
    await onChange(newMenuList);
    setActiveMenuList(getMenuList(newMenuList, activeMenuListNest));
  };

  const handleSelectMenu  = async (menu: Menu): Promise<void> => {
    await handleAddMenu({
      id: menu.id,
      name: menu.name,
      menuTypeId: menu.menuTypeId,
      menuMaterialId: menu.menuMaterialId,
      menuMaterialName: menu.menuMaterialName,
      vhPresentationId: menu.vhPresentationId,
      taskCreateFlg: false,
      taskCoopLabelId: null,
      taskCoopLabelName: null,
      enabledFlg: true,
      menuOrder: 0,
      subMenuList: [],
    });
  };

  const handleDeleteMenuClick = useCallback(async (deleteRowIndex: number): Promise<void> => {
    const newMenuList = filterMenu(menuList, activeMenuListNest.concat(deleteRowIndex));
    await deleteSelectedItems(activeMenuList[deleteRowIndex]);
    await onChange(newMenuList);
    setActiveMenuList(getMenuList(newMenuList, activeMenuListNest));
  }, [menuList, activeMenuList, activeMenuListNest, filterMenu, getMenuList, deleteSelectedItems, onChange]);

  const changeActiveMenuList = async (list: MenuSetMenu[], nest: number[]): Promise<void> => {
    if (nest.length > 0) {
      const index = nest[0];
      if (typeof index !== 'undefined') {
        await changeActiveMenuList(
          list[index].subMenuList, nest.slice(1)
        );
      } else {
        // ToDo: エラーハンドリング、Errorクラス、エラーメッセージを適切なものに
        throw new Error('存在しないネストが指定されました。');
      }

      return;
    }

    setActiveMenuList(list);
  };

  const handleChangeActiveMenuList = async (nest: number[]): Promise<void> => {
    setActiveMenuListNest(nest);
    await changeActiveMenuList(menuList, nest);
  };

  const handleMoveUpMenu = useCallback(async (rowIndex: number): Promise<void> => {
    const newActiveList = arrayMove(activeMenuList, rowIndex, rowIndex - 1)
      .map((menu, index) => {
        if ((index === rowIndex) || (index === (rowIndex - 1))) {
          return {
            ...menu,
            menuOrder: index,
          }
        }

        return menu;
      });

    const newMenuList = trasnsMenuList(menuList, activeMenuListNest, newActiveList);

    await onChange(newMenuList);
    setActiveMenuList(newActiveList);
  }, [menuList, activeMenuList, activeMenuListNest, trasnsMenuList, onChange]);

  const handleMoveDownMenu = useCallback(async (rowIndex: number): Promise<void> => {
    const newActiveList = arrayMove(activeMenuList, rowIndex, rowIndex + 1)
      .map((menu, index) => {
        if ((index === rowIndex) || (index === (rowIndex + 1))) {
          return {
            ...menu,
            menuOrder: index,
          }
        }

        return menu;
      });
    const newMenuList = trasnsMenuList(menuList, activeMenuListNest, newActiveList);

    await onChange(newMenuList);
    setActiveMenuList(newActiveList);
  }, [menuList, activeMenuList, activeMenuListNest, trasnsMenuList, onChange]);

  const EnabledFlgCell = useCallback(({ value }: CellProps<DataType>) => {
    return (
      <>
        { value ? <div>有効</div> : <div>無効</div> }
      </>
    );
  }, []);

  const MenuTypeCell = useCallback(({ value }: CellProps<DataType>) => {
    return (
      <>
        <div>{ getMenuTypeText(value) }</div>
      </>
    );
  }, []);

  const OperationCell = useCallback(({ row, rows } : CellProps<MenuSetMenu>) => {

    return (
      <>
        <button className="btn-primary min-w-0 w-20 mb-1"
                type='button'
                onClick={async () => {
                  await handleEditMenuClick(row.index);
                }}
        >
          編集
        </button>
        {
          row.index !== 0 && (
            <button
              className="bg-primary py-1 px-2 ml-1 text-white font-bold hover:opacity-80 rounded-full focus:outline-none"
              type='button'
              onClick={() => handleMoveUpMenu(row.index)}
            >
              ▲
            </button>
          )
        }
        {
          ((rows.length - 1) !== row.index) && (
            <button
              className="bg-primary py-1 px-2 ml-1 text-white font-bold hover:opacity-80 rounded-full focus:outline-none"
              type='button'
              onClick={() => handleMoveDownMenu(row.index)}
            >
              ▼
            </button>
          )
        }
        <button className="bg-gray-500 py-1 px-2 ml-1 text-white font-bold hover:opacity-80 rounded-full focus:outline-none"
                type='button'
                onClick={() => handleDeleteMenuClick(row.index)}
        >
          ×
        </button>
      </>
    );
  }, [handleEditMenuClick, handleDeleteMenuClick, handleMoveUpMenu, handleMoveDownMenu]);

  const columns: Column<DataType>[]  = React.useMemo(
    () => [
      {
        Header: '名称',
        accessor: 'name',
      },
      {
        Header: '有効',
        accessor: 'enabledFlg',
        Cell: EnabledFlgCell,
      },
      {
        Header: 'メニュータイプ',
        accessor: 'menuTypeId',
        Cell: MenuTypeCell,
      },
      {
        Header: '操作',
        accessor: 'operation',
        Cell: OperationCell,
      },
    ],
    [EnabledFlgCell, MenuTypeCell, OperationCell]
  );

  return (
    <>
      {showMenuAddMethodSelectModal && (
        <MenuAddMethodSelectModal
          {...{
            setShowModal: setShowMenuAddMethodSelectModal,
            onCreateMenuClick: handleCreateMenuClick,
            onSelectMenuClick: handleSelectMenuClick,
          }}
        />
      )}
      {showSelectMenuModal && (
        <SelectMenuModal
          {...{
            selectedMenuIdMap,
            selectedMenuMaterialIdMap,
            selectedVhPresentationIdMap,
            setShowModal: setShowSelectMenuModal,
            onSelect: handleSelectMenu,
            onSelected: async () => {
              setShowSelectMenuModal(false);
              setShowMenuAddMethodSelectModal(false)
            },
          }}
        />
      )}
      {showMenuFormModal && (
        <MenuFormModal
          {...{
            isEdit: isMenuFormEditMode,
            defaultValues: defaultMenuFormValues,
            labelSetId,
            selectedMenuMaterialIdMap,
            selectedVhPresentationIdMap,
            setShowModal: setShowMenuFormModal,
            onSave: isMenuFormEditMode ? handleUpdateMenu : handleAddMenu,
            onSaved: async () => {
              setShowMenuFormModal(false);
              if (!isMenuFormEditMode) {
                setShowMenuAddMethodSelectModal(false);
              }
            }
          }}
        />
      )}
      <div className="py-2">
        <div className='flex items-center'>
          <div className="text-sm font-semibold text-primary">
            <span>メニュー</span>
            <span className="ml-2 required">必須</span>
          </div>
          <div className='ml-auto'>
            <button className={`btn-primary ml-auto ${ typeof labelSetId === 'undefined' ? 'btn-primary-disabled' : '' } `}
                    type='button'
                    onClick={async () => {
                      await handleShowAddMenuMethodSelectModalClick()
                    }}
                    disabled={ typeof labelSetId === 'undefined' }
            >
              追 加
            </button>
          </div>
        </div>
      </div>
      <div className="flex">
        <div className='w-1/4'>
          <MenuListPreview
            menuList={menuList}
            nest={[]}
            onChangeViewMenuList={handleChangeActiveMenuList}
            activeNest={activeMenuListNest}
          />
        </div>
        <div className='w-3/4 ml-4 overflow-scroll'>
          <Table
            tableData={activeMenuList}
            columns={columns}
          />
        </div>
      </div>
    </>
  );
};
